import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import LandingPage from './features/landing/LandingPage';
import DashboardPage from './features/dashboard/DashboardPage';
import AgendaView from './features/agenda/AgendaView';
import EmployeeListPage from './features/employees/EmployeeListPage';
import ClientListPage from './features/clients/ClientListPage';
import ServiceListPage from './features/services/ServiceListPage';
import FinancesPage from './features/finances/FinancesPage';
import PdfDemoPage from './features/finances/PdfDemoPage';
import InventoryPage from './features/inventory/InventoryPage';
import PublicBookingPage from './features/public-booking/PublicBookingPage';
import SettingsPage from './features/settings/SettingsPage';
import BookingApprovalPanel from './features/booking-approval/BookingApprovalPanel';
import HelpPage from './features/help/HelpPage';
import { UpsellModalProvider } from './components/UpsellModal';
import { PaymentWallProvider } from './components/PaymentWall';

import { InstallPWA } from './components/pwa/InstallPWA';
import TermsAndConditions from './features/legal/TermsAndConditions';
import PrivacyPolicy from './features/legal/PrivacyPolicy';
import SubscriptionSuccessPage from './features/subscription/SubscriptionSuccessPage';
import CampaignsPage from './features/campaigns/CampaignsPage';
import AdminDashboard from './features/admin/AdminDashboard';
import AdminGuard from './features/admin/AdminGuard';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/Toast/ToastContainer';
import ScrollToTop from './components/ScrollToTop';

/** Read basic user info from localStorage to pass to the PaymentWall */
function getUserInfo() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return { userEmail: '', salonName: '' };
    const u = JSON.parse(raw);
    return {
      userEmail: u.email || '',
      salonName: u.salonName || u.name || 'Mi Salón',
    };
  } catch {
    return { userEmail: '', salonName: '' };
  }
}

function App() {
  const { userEmail, salonName } = getUserInfo();
  return (
    <ToastProvider>
      <UpsellModalProvider>
        <InstallPWA />
        <ToastContainer />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo" element={<Navigate to="/" replace />} />
            <Route path="/precios" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="/verify" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/" replace />} />
            <Route path="/reset-password" element={<Navigate to="/" replace />} />
            <Route path="/validate-demo" element={<Navigate to="/" replace />} />
            <Route path="/accept-invitation" element={<Navigate to="/" replace />} />
            <Route path="/onboarding" element={<Navigate to="/" replace />} />

            {/* Help Center - Accessible from anywhere */}
            <Route path="/help" element={<HelpPage />} />

            {/* Public Booking Page - /book/:salonSlug */}
            <Route path="/book/:salonSlug" element={<PublicBookingPage />} />
            <Route path="/book/:salonSlug/e/:employeeId" element={<PublicBookingPage />} />

            {/* Legal Pages */}
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Authenticated App Routes — wrapped in PaymentWall */}
            <Route
              path="/app"
              element={
                <PaymentWallProvider userEmail={userEmail} salonName={salonName}>
                  <MainLayout />
                </PaymentWallProvider>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="agenda" element={<AgendaView />} />
              <Route path="employees" element={<EmployeeListPage />} />
              <Route path="clients" element={<ClientListPage />} />
              <Route path="services" element={<ServiceListPage />} />
              <Route path="finances" element={<FinancesPage />} />
              <Route path="pdf-demo" element={<PdfDemoPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="booking-approvals" element={<BookingApprovalPanel />} />
              <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
            </Route>

            {/* Admin Routes */}
            <Route element={<AdminGuard />}>
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </UpsellModalProvider>
    </ToastProvider>
  );
}

export default App;

